import { zodResolver } from '@hookform/resolvers/zod';
import {
  eSignContractStatusOptions,
  stockAccountStatusOptions,
} from 'apps/investment/constants/stock-accounts';
import { Button } from 'common/ui/button';
import { Input } from 'common/ui/input';
import { Label } from 'common/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'common/ui/select';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { sanitizeFormData } from 'utils/filter-form';
import { z } from 'zod';

const filterSchema = z.object({
  status: z.string().optional(),
  source: z.string().optional(),
  esign_contract_status: z.string().optional(),
  search: z.string().optional(),
});

type FilterFormValues = {
  status?: string;
  source?: string;
  esign_contract_status?: string;
  search?: string;
};

export const FilterForm = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    status,
    search,
    source,
    esign_contract_status: esignContractStatus,
    ...queryParams
  } = Object.fromEntries(searchParams.entries());

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      status: status || 'all',
      source: source || 'all',
      esign_contract_status: esignContractStatus || 'all',
      search: search || '',
    },
  });

  const onSubmit = (data: FilterFormValues) => {
    const sanitizedData = sanitizeFormData(data);
    setSearchParams({
      ...queryParams,
      ...sanitizedData,
    });
  };

  const handleReset = () => {
    reset({
      status: 'all',
      source: 'all',
      esign_contract_status: 'all',
      search: '',
    });
    setSearchParams(queryParams);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-col gap-1 min-w-[200px]">
          <Label htmlFor="search">Search</Label>
          <Controller
            name="search"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                id="search"
                placeholder="Search..."
                value={field.value || ''}
                onChange={e => field.onChange(e.target.value)}
              />
            )}
          />
          {errors.search && (
            <p className="text-red-500 text-sm">{errors.search.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1 min-w-[100px]">
          <Label htmlFor="Status">Trạng thái</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={value => field.onChange(value || 'all')}
                value={field.value || 'all'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[
                    {
                      label: 'All',
                      value: 'all',
                    },
                    ...stockAccountStatusOptions,
                  ].map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[100px]">
          <Label htmlFor="source">Nguồn</Label>
          <Controller
            name="source"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={value => field.onChange(value || 'all')}
                value={field.value || 'all'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="nhsv">NHSV</SelectItem>
                  <SelectItem value="stag">Stag</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[150px]">
          <Label htmlFor="esign_contract_status">Trạng thái hợp đồng</Label>
          <Controller
            name="esign_contract_status"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={value => field.onChange(value || 'all')}
                value={field.value || 'all'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[
                    { label: 'All', value: 'all' },
                    ...eSignContractStatusOptions,
                  ].map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div className="flex gap-1 self-end">
          <Button type="submit">Filter</Button>
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
};
